import { MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import { LocalStorageKeys } from "../enums/localStorageKeys";
import { getAllJCodes } from "../pages/singleOrderLayout/utils/existingDocsToLs";
import { setItemInLocalStorage } from "./localStorageHelper";

/**
 * Stores all extracted J-codes from a Medical PA Order into localStorage.
 *
 * This function retrieves all J-codes associated with the given medical PA order,
 * normalizes them into an array of strings, and saves them under a unique key in
 * localStorage. If no J-codes are found, an empty array is stored instead.
 *
 * @function setJCodesInLocalStorage
 * @param {MedicalPaOrder} singleMedicalPaOrder - The medical PA order containing J-code information.
 * @returns {void} This function does not return a value.
 *
 * @example
 * const order = { id: "123", ... };
 * setJCodesInLocalStorage(order);
 * // localStorage["J_CODES_123"] = ["J1234", "J5678"];
 *
 * @remarks
 * - Gracefully handles errors and logs them to the console.
 * - Filters out null/undefined J-codes and replaces them with empty strings.
 * - Ensures localStorage always receives a valid array (never null or undefined).
 */

export const setJCodesInLocalStorage = (
  singleMedicalPaOrder: MedicalPaOrder,
) => {
  try {
    const jCodes = getAllJCodes(singleMedicalPaOrder);
    if (jCodes && jCodes.length > 0) {
      const jCodesArray = jCodes.map((jCode) => jCode?.j_code ?? "");
      setItemInLocalStorage(
        `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
        jCodesArray ?? [],
      );
    } else {
      setItemInLocalStorage(
        `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
        [],
      );
    }
  } catch (error) {
    console.error("Error setting J codes in local storage:", error);
  }
};
